/**
 * <NukkiCanvas> — 캔버스만. 스키닝 4단계(완전 커스텀 UI)의 조각.
 *
 * 코어가 소유한 캔버스 엘리먼트를 스테이지에 붙이고 포인터 핸들러를 건다.
 * 툴바는 호스트가 원하는 대로 그린다.
 */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { UseNukkiReturn } from './useNukki.js';

export interface NukkiCanvasProps {
  nukki: UseNukkiReturn;
  className?: string | undefined;
  label?: string | undefined;
  /** 스테이지 위에 겹칠 것 (로딩 오버레이 등). */
  children?: ReactNode;
}

export function NukkiCanvas({ nukki, className, label = '편집 중인 이미지', children }: NukkiCanvasProps) {
  const stage = useRef<HTMLDivElement | null>(null);

  // React 의 onWheel 은 루트에 passive 로 붙을 수 있어 preventDefault 가 무시된다.
  // 확대·이동에서 페이지 스크롤을 막으려면 네이티브 non-passive 로 붙여야 한다.
  const { onWheel } = nukki;
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // 캔버스는 **코어가 만든 엘리먼트**라 React 가 style prop 을 꽂을 수 없다.
  // 변환을 직접 써 준다. 이걸 빼면 배율 상태만 바뀌고 화면은 그대로다.
  const { scale, x, y } = nukki.transform;
  useEffect(() => {
    const c = nukki.canvasRef.current;
    if (!c) return;
    c.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    c.style.transformOrigin = '0 0';
    c.style.touchAction = 'none';
  });

  return (
    <div
      className={['nk-stage', className].filter(Boolean).join(' ')}
      ref={(host) => {
        stage.current = host;
        nukki.attachCanvas(host);
        const c = nukki.canvasRef.current;
        if (c) {
          c.setAttribute('role', 'img');
          c.setAttribute('aria-label', label);
        }
      }}
      // 포인터 핸들러는 캔버스가 아니라 여기서 위임받는다 -- 캔버스는 코어가
      // 만든 엘리먼트라 React 가 props 를 꽂을 수 없다.
      onPointerDown={nukki.canvasProps.onPointerDown as never}
      onPointerMove={nukki.canvasProps.onPointerMove as never}
      onPointerUp={nukki.canvasProps.onPointerUp as never}
      onPointerCancel={nukki.canvasProps.onPointerCancel as never}
      style={{ touchAction: 'none' }}
    >
      {children}
    </div>
  );
}
